import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import axios from 'axios';
import { FiBarChart2, FiAlertCircle } from 'react-icons/fi';
import ErrorBoundary from '../components/ErrorBoundary';
import SFRTPaymentAnalytics from '../components/SFRTPaymentAnalytics';

const SFRTDashboardContent = () => {
    const [stats, setStats] = useState({
        sft: {
            totalTrx: 0,
            totalAmountSGD: 0
        },
        srt: {
            totalTrx: 0,
            totalAmountSGD: 0
        }
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchStats = async () => {
            try {
                setLoading(true);
                const token = localStorage.getItem('token');
                
                // Fetch all SFRT payments
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/api/sfrtpayments?limit=10000`,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'ngrok-skip-browser-warning': 'true'
                        }
                    }
                );

                if (response.data.success) {
                    const payments = response.data.data;
                    
                    // Calculate SFT stats
                    const sftPayments = payments.filter(p => p.category === 'SFT');
                    const sftTotalTrx = sftPayments.length;
                    const sftTotalAmountSGD = sftPayments.reduce((sum, p) => sum + (p.amountSGD || 0), 0);
                    
                    // Calculate SRT stats
                    const srtPayments = payments.filter(p => p.category === 'SRT');
                    const srtTotalTrx = srtPayments.length;
                    const srtTotalAmountSGD = srtPayments.reduce((sum, p) => sum + (p.amountSGD || 0), 0);

                    setStats({
                        sft: {
                            totalTrx: sftTotalTrx,
                            totalAmountSGD: sftTotalAmountSGD
                        },
                        srt: {
                            totalTrx: srtTotalTrx,
                            totalAmountSGD: srtTotalAmountSGD
                        }
                    });
                }
            } catch (err) {
                setError(`Failed to fetch statistics: ${err.response?.data?.error || err.message}`);
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
    }, []);

    const formatAmount = (amount) => {
        return new Intl.NumberFormat('en-SG', {
            style: 'currency',
            currency: 'SGD'
        }).format(amount);
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 w-full max-w-none px-8">
            <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-semibold mb-6">SFRT Dashboard Overview</h2>

                {error && (
                    <div className="mb-6 p-4 bg-red-100 text-red-700 rounded-md flex items-center">
                        <FiAlertCircle className="mr-2" />
                        {error}
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* SFT Total Card */}
                    <div className="bg-orange-50 p-4 rounded-lg">
                        <div className="flex items-center">
                            <FiBarChart2 className="h-8 w-8 text-orange-600" />
                            <div className="ml-4">
                                <h3 className="text-lg font-medium text-orange-800">SFT Total</h3>
                            </div>
                        </div>
                        <div className="mt-4 space-y-2">
                            <div className="flex justify-between text-sm">
                                <span className="text-orange-700">Total Trx</span>
                                <span className="font-medium text-orange-800">{stats.sft.totalTrx}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-orange-700">Total Amount in SGD</span>
                                <span className="font-medium text-orange-800">
                                    {formatAmount(stats.sft.totalAmountSGD)}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* SRT Total Card */}
                    <div className="bg-pink-50 p-4 rounded-lg">
                        <div className="flex items-center">
                            <FiBarChart2 className="h-8 w-8 text-pink-600" />
                            <div className="ml-4">
                                <h3 className="text-lg font-medium text-pink-800">SRT Total</h3>
                            </div>
                        </div>
                        <div className="mt-4 space-y-2">
                            <div className="flex justify-between text-sm">
                                <span className="text-pink-700">Total Trx</span>
                                <span className="font-medium text-pink-800">{stats.srt.totalTrx}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-pink-700">Total Amount in SGD</span>
                                <span className="font-medium text-pink-800">
                                    {formatAmount(stats.srt.totalAmountSGD)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* SFRT Payment Analytics Section */}
            <SFRTPaymentAnalytics />
        </div>
    );
};

const SFRTDashboard = () => {
    return (
        <Layout>
            <ErrorBoundary
                fallbackTitle="SFRT Dashboard Error"
                fallbackDescription="An error occurred while loading the SFRT dashboard. Please try again or contact support if the problem persists."
            >
                <SFRTDashboardContent />
            </ErrorBoundary>
        </Layout>
    );
};

export default SFRTDashboard;
